import React from 'react';
import { AssetFlowBreakdown } from '../../api/metrics';

interface AssetBreakdownProps {
  assetBreakdown: AssetFlowBreakdown[];
}

const AssetBreakdown: React.FC<AssetBreakdownProps> = ({ assetBreakdown }) => {
  if (!assetBreakdown || assetBreakdown.length === 0) {
    return (
      <div className="asset-breakdown empty">
        <p>No asset data available</p>
      </div>
    );
  }

  const formatCurrency = (value: number) => {
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `$${(value / 1000).toFixed(0)}k`;
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatRatio = (ratio: number) => {
    if (ratio > 100) return '99.9x';
    if (ratio === 0) return '0x';
    return `${ratio.toFixed(1)}x`;
  };

  // Sort by total volume (buys + sells) descending
  const sortedAssets = [...assetBreakdown].sort((a, b) => {
    const aTotal = a.buyVolume + a.sellVolume;
    const bTotal = b.buyVolume + b.sellVolume;
    return bTotal - aTotal;
  });

  return (
    <div className="asset-breakdown">
      <h4>Asset Trading Breakdown</h4>
      <div className="asset-table">
        <div className="asset-table-header">
          <div className="asset-header-cell asset-name-col">Asset</div>
          <div className="asset-header-cell asset-flow-col">Trading Flow</div>
          <div className="asset-header-cell asset-ratio-col">Buy/Sell Ratio</div>
          <div className="asset-header-cell asset-orders-col">Order Count</div>
        </div>
        <div className="asset-table-body">
          {sortedAssets.map((asset) => {
            const totalVolume = asset.buyVolume + asset.sellVolume;
            const buyPercent = totalVolume > 0 ? (asset.buyVolume / totalVolume) * 100 : 0;
            const sellPercent = totalVolume > 0 ? (asset.sellVolume / totalVolume) * 100 : 0;

            return (
              <div key={asset.productId} className="asset-table-row">
                <div className="asset-cell asset-name-col">
                  <span className="product-name">{asset.productId}</span>
                </div>

                <div className="asset-cell asset-flow-col">
                  <div className="mini-flow-container">
                    <div className="mini-flow-bar">
                      <div
                        className="mini-flow-buy"
                        style={{ width: `${buyPercent}%` }}
                        title={`Buys: ${formatCurrency(asset.buyVolume)} (${buyPercent.toFixed(1)}%)`}
                      ></div>
                      <div
                        className="mini-flow-sell"
                        style={{ width: `${sellPercent}%` }}
                        title={`Sells: ${formatCurrency(asset.sellVolume)} (${sellPercent.toFixed(1)}%)`}
                      ></div>
                    </div>
                    <div className="mini-flow-labels">
                      <span className="buy-label">{formatCurrency(asset.buyVolume)}</span>
                      <span className="flow-divider">|</span>
                      <span className="sell-label">{formatCurrency(asset.sellVolume)}</span>
                    </div>
                  </div>
                </div>

                <div className="asset-cell asset-ratio-col">
                  <span className={`ratio-badge ${asset.ratio > 1.5 ? 'buy-heavy' : asset.ratio < 0.67 ? 'sell-heavy' : 'balanced'}`}>
                    {formatRatio(asset.ratio)}
                  </span>
                </div>

                <div className="asset-cell asset-orders-col">
                  <div className="order-counts">
                    <span className="buy-orders">{asset.buyOrderCount}</span>
                    <span className="order-divider">/</span>
                    <span className="sell-orders">{asset.sellOrderCount}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AssetBreakdown;
